import {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";

export type PositionBilan = {
  x: number;
  y: number;
  id: string;
  column: number;
  row: number;
};

export interface ResultBilan {
  position: string;
  reactionTime: number | string;
}

interface BilanProps {
  caseSize: number;
  isPaused: boolean;
  setFinalResults: (results: ResultBilan[]) => void;
  toggleFullScreen: () => void;
  setIsPaused: (isPaused: boolean) => void;
  saveFieldSession: (fieldsToUpdate: { field: string; value: string | number | number[] | Date | null; }[]) => void;
  status: "EN_COURS" | "PREVUE" | "REALISEE";
  targetEnCours_Zf_Bilan: PositionBilan[] | null;
  resultsBilan: ResultBilan[] | null;
}

const Bilan = forwardRef(
  (
    {
      caseSize,
      isPaused,
      setFinalResults,
      toggleFullScreen,
      setIsPaused,
      saveFieldSession,
      status,
      targetEnCours_Zf_Bilan,
      resultsBilan
    }: BilanProps,
    ref
  ) => {
    const [currentTarget, setCurrentTarget] = useState<PositionBilan | null>(null);
    const [targetColor, setTargetColor] = useState("white");
    const [startTime, setStartTime] = useState<number | null>(null);
    const [results, setResults] = useState<ResultBilan[]>(resultsBilan || []);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const vitesse = useRef(1);
    const [ciblesRestantes, setCiblesRestantes] = useState(3 * 192);
    const [canShowTarget, setCanShowTarget] = useState(true);
    const [quadrillageVisible, setQuadrillageVisible] = useState(false);
    const targetsSinceLastPause = useRef(0);
    const displayedTargetIdsRef = useRef<Set<string>>(new Set());

    const cellWidth = (5.5 / 4) * caseSize;
    const cellHeight = caseSize;

    const targetPositionsRef = useRef<PositionBilan[]>([]);
    const isPausedRef = useRef(isPaused);
    const hasReactedRef = useRef(false);

    useEffect(() => {
      isPausedRef.current = isPaused;
    }, [isPaused]);

    // Ref to store the timer references
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const timerRef2 = useRef<NodeJS.Timeout | null>(null);

    // Ref to store the showNewTarget function
    const showNewTargetRef = useRef(() => {});

    const calculateCanvasDimensions = useCallback(() => {
      const maxCanvasWidth = window.innerWidth;
      const maxCanvasHeight = window.innerHeight;

      const totalColumns = 16;
      const totalRows = 12;

      let maxColumns = totalColumns;
      let maxRows = totalRows;

      let canvasWidth = maxColumns * cellWidth;
      let canvasHeight = maxRows * cellHeight;

      let columnsToRemove = 0;
      if (canvasWidth > maxCanvasWidth) {
        const excessColumns = Math.ceil(
          (canvasWidth - maxCanvasWidth) / cellWidth
        );
        columnsToRemove = Math.ceil(excessColumns / 2);
        maxColumns -= 2 * columnsToRemove;
        canvasWidth = maxColumns * cellWidth;
      }

      let rowsToRemove = 0;
      if (canvasHeight > maxCanvasHeight) {
        const excessRows = Math.ceil(
          (canvasHeight - maxCanvasHeight) / cellHeight
        );
        rowsToRemove = Math.ceil(excessRows / 2);
        maxRows -= 2 * rowsToRemove;
        canvasHeight = maxRows * cellHeight;
      }

      return {
        canvasWidth,
        canvasHeight,
        maxColumns,
        maxRows,
        columnsToRemove,
        rowsToRemove,
      };
    }, [cellWidth, cellHeight, window.innerWidth, window.innerHeight]);

    const { canvasWidth, canvasHeight } = calculateCanvasDimensions();

    useEffect(() => {
      const totalColumns = 16;
      const totalRows = 12;

      const newPositions: PositionBilan[] = [];
      if (status === 'EN_COURS' && resultsBilan && targetEnCours_Zf_Bilan !== null) {
        newPositions.push(...targetEnCours_Zf_Bilan);
      } else if(status ==='PREVUE' || status === 'EN_COURS' && resultsBilan === null && targetEnCours_Zf_Bilan === null) {
      const ciblesParMaillage = 3;
      const espacementX = cellWidth;
      const espacementY = cellHeight;

      // Calcul de toutes les positions sur la grille 12x16
      for (let ligne = 0; ligne < totalRows; ligne++) {
        for (let colonne = 0; colonne < totalColumns; colonne++) {
          const cibleX = colonne * espacementX;
          const cibleY = ligne * espacementY;
          for (let j = 0; j < ciblesParMaillage; j++) {
            newPositions.push({
              x: cibleX,
              y: cibleY,
              id: `${ligne}-${colonne}-${j}`,
              column: colonne,
              row: ligne,
            });
          }
        }
      }
    } 

      // Filtrage des positions en fonction de la taille de l'écran pour maintenir la symétrie
      const { columnsToRemove, rowsToRemove } = calculateCanvasDimensions();

      const startColumn = columnsToRemove;
      const endColumn = totalColumns - columnsToRemove;

      const startRow = rowsToRemove;
      const endRow = totalRows - rowsToRemove;

      const filteredPositions = newPositions.filter((position) => {
        return (
          position.column >= startColumn &&
          position.column < endColumn &&
          position.row >= startRow &&
          position.row < endRow &&
          !displayedTargetIdsRef.current.has(position.id)
        );
      });

      filteredPositions.sort(() => Math.random() - 0.5);
      targetPositionsRef.current = filteredPositions;

      setCiblesRestantes(filteredPositions.length);
    }, [caseSize, calculateCanvasDimensions]);

    const convertToGridNotation = useCallback((position: PositionBilan) => {
      const columnLetter = String.fromCharCode(65 + position.column);
      const rowNumber = position.row + 1;
      return `${columnLetter}${rowNumber}`;
    }, []);

    useEffect(() => {
      const handleKeyPress = (e: { key: string }) => {
        if (
          e.key === " " &&
          currentTarget &&
          !isPaused &&
          startTime !== null &&
          !hasReactedRef.current
        ) {
          hasReactedRef.current = true;
          const reactionTime = new Date().getTime() - startTime;
          const gridPosition = convertToGridNotation(currentTarget);
          if (reactionTime <= 800 / vitesse.current) {
            setResults((prevResults) => [
              ...prevResults,
              { position: gridPosition, reactionTime },
            ]);
            if (timerRef2.current !== null) {
              clearTimeout(timerRef2.current);
            }
          }
        } else if (e.key === "q" || e.key === "Q") {
          console.log("Quadrillage visible :", quadrillageVisible);
          setQuadrillageVisible((prev) => !prev);
        } else if (e.key === "r" || e.key === "R") {
          vitesse.current = vitesse.current === 1 ? 25 : 1;
        }
      };

      window.addEventListener("keydown", handleKeyPress);
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    }, [currentTarget, startTime, isPaused, caseSize, convertToGridNotation]);

    useEffect(() => {
      const handleKeyPress = (event: { key: string }) => {
        const displayResults = () => {
          console.log(results);
        };

        if (event.key === "j") {
          displayResults();
        }
      };

      window.addEventListener("keydown", handleKeyPress);

      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    });

    const showNewTarget = useCallback(() => {
      // console.log("showNewTarget called", targetPositionsRef.current.length, ciblesRestantes, canShowTarget, isPausedRef.current);
      if (
        targetPositionsRef.current.length > 0 &&
        ciblesRestantes > 0 &&
        canShowTarget &&
        !isPausedRef.current
      ) {
        setCanShowTarget(false);
        const delai = (Math.random() * 2000 + 1000) / vitesse.current; // Délai aléatoire entre 1000 et 3000 ms

        timerRef.current = setTimeout(() => {
          if (isPaused) return;

          const newTarget = targetPositionsRef.current.pop();
          if (!newTarget) {
            return;
          }
          setCurrentTarget(newTarget);
          displayedTargetIdsRef.current.add(newTarget.id);
          hasReactedRef.current = false;
          setTargetColor("white");
          setStartTime(new Date().getTime());

          setTimeout(() => {
            if (isPaused) return; // Si le jeu est en pause, ne change pas la couleur de la cible
            setTargetColor("black");
          }, 150 / vitesse.current);

          timerRef2.current = setTimeout(() => {
            if (isPausedRef.current) {
              // Si le jeu est en pause, ne marque pas la cible comme "Trop long"
              return;
            }

            const gridPosition = convertToGridNotation(newTarget);
            setResults((prevResults) => [
              ...prevResults,
              { position: gridPosition, reactionTime: "Trop long" },
            ]);
          }, 800 / vitesse.current);

          setTimeout(() => {
            if (isPausedRef.current) {
              // Si le jeu est en pause, ne retire pas la cible courante
              return;
            }

            setCurrentTarget(null);
            setCanShowTarget(true);
            setCiblesRestantes((prev) => prev - 1);
            if (
              targetsSinceLastPause.current >= 192 ||
              !document.fullscreenElement
            ) {
              setIsPaused(true);
              isPausedRef.current = true;
              targetsSinceLastPause.current = 0;
            } else if (
              targetPositionsRef.current.length > 0 &&
              ciblesRestantes > 0 &&
              !isPausedRef.current
            ) {
              // console.log(
              //   "taregtsSinceLastPause",
              //   targetsSinceLastPause.current
              // );
              showNewTarget();
              targetsSinceLastPause.current += 1;
            }
            // }, 80);
          }, 800 / vitesse.current);
        }, delai);
      } else {
        console.info("showNewTarget not executed");
      }
    }, [
      isPaused,
      canShowTarget,
      ciblesRestantes,
      targetPositionsRef,
      showNewTargetRef,
      convertToGridNotation,
      setCanShowTarget,
    ]);
    useEffect(() => {
      return () => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
        }
        if (timerRef2.current !== null) {
          clearTimeout(timerRef2.current);
        }
      };
    }, []);

    const resumeGame = useCallback(() => {
      if (!document.fullscreenElement) {
        toggleFullScreen();
      }
      setIsPaused(false);
      isPausedRef.current = false;
      showNewTarget();
    }, [toggleFullScreen]);

    // Save the showNewTarget function to the ref
    showNewTargetRef.current = showNewTarget;

    useImperativeHandle(ref, () => ({
      start: () => {
        showNewTargetRef.current();
      },
      resume: () => {
        resumeGame();
      },
    }));

    useEffect(() => {
      return () => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
        }
      };
    }, []);

    useEffect(() => {
      const { canvasWidth, canvasHeight } = calculateCanvasDimensions();

      const canvas = canvasRef.current;
      if (canvas) {
        const context = canvas.getContext("2d");
        if (context) {
          context.fillStyle = "black";
          context.fillRect(0, 0, canvasWidth, canvasHeight);

          if (quadrillageVisible) {
            // Optional: Drawing grid lines
            context.strokeStyle = "white";
            context.lineWidth = 1;

            // Draw vertical grid lines
            for (let x = 0; x <= canvasWidth; x += cellWidth) {
              context.beginPath();
              context.moveTo(x, 0);
              context.lineTo(x, canvasHeight);
              context.stroke();
            }

            // Draw horizontal grid lines
            for (let y = 0; y <= canvasHeight; y += cellHeight) {
              context.beginPath();
              context.moveTo(0, y);
              context.lineTo(canvasWidth, y);
              context.stroke();
            }
          }

          const centerX = canvasWidth / 2;
          const centerY = canvasHeight / 2;
          const crossLength = (0.7 * cellHeight) / 4;

          context.beginPath();
          context.strokeStyle = "white";
          context.moveTo(centerX - crossLength / 2, centerY);
          context.lineTo(centerX + crossLength / 2, centerY);
          context.stroke();

          context.beginPath();
          context.moveTo(centerX, centerY - crossLength / 2);
          context.lineTo(centerX, centerY + crossLength / 2);
          context.stroke();

          if (currentTarget) {
            const radius = cellHeight / 8; // Adjusted for rectangular cells
            const targetCenterX = currentTarget.x + cellWidth / 2;
            const targetCenterY = currentTarget.y + cellHeight / 2;

            context.fillStyle = targetColor;
            context.beginPath();
            context.arc(targetCenterX, targetCenterY, radius, 0, Math.PI * 2);
            context.fill();
          }
        }
      }
    }, [
      currentTarget,
      targetColor,
      calculateCanvasDimensions,
      cellWidth,
      cellHeight,
    ]);

    useEffect(() => {

      const shouldSaveIntermediateResult = targetPositionsRef.current.length % 5 === 0 && targetPositionsRef.current.length > 0;
      if (shouldSaveIntermediateResult) {
        const fieldsToUpdate = [
          { field: "resultsBilan", value: JSON.stringify(results) },
          { field: "targetEnCours_Zf_Bilan", value: JSON.stringify(targetPositionsRef.current) },
        ];
        saveFieldSession(fieldsToUpdate)
      }

      if (targetPositionsRef.current.length === 0) {
        setFinalResults(results);
      }
    }, [results, setFinalResults, targetPositionsRef]);

    const cursorClassVisibility = isPaused ? "cursor-pointer" : "";

    return (
      <div className={cursorClassVisibility}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "black",
        }}
      >
        <canvas ref={canvasRef} width={canvasWidth} height={canvasHeight} />
        {isPaused && (
          <button
            onClick={resumeGame}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              padding: "10px 20px",
              backgroundColor: "white",
              border: "none",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Reprendre
          </button>
        )}
      </div>
    );
  }
);

export default Bilan;
